import axios from '@/plugins/axios'
import customErrors from '@/store/error'
import {currencySymbols, formatMetrics} from '@/helpers/variables/currencies'
import {v4 as uuidv4} from 'uuid'
import {fileDownload} from '@/helpers/fileDownload'
import i18n from '@/i18n/i18n'
import {STATISTIC_COLUMNS} from '@/helpers/permissions/common'
import {addFilter, createParams, isDuplicateFilter, updateFilter} from '@/helpers/filterHelper'
import {DEFAULT_SIZE} from '@/helpers/table'

const TYPE_LOADING = 'ITEMS'
const TYPE_LOADING_ACTION = 'ACTION'

const DEFAULT_PARAMS = {
  page: 0,
  size: undefined,
  sort: 'date,desc',
  isClientsStatistics: undefined,
  userId: undefined,
  isShowTotal: false,
}

let controller = null

const state = () => ({
  items: [],
  groups: [],
  total: null,
  loading: false,
  actionLoading: false,
  dataPagination: {},
  params: {...DEFAULT_PARAMS},
  isShowFilterBar: false,
  selectedAppFilter: {},
  selectedTableFilters: [],
})

const getters = {
  permission: (state, getters, rootState) => {
    return {
      read: rootState.auth?.permissions?.some((a) => a === 'STATISTIC_READ'),
      readAll: rootState.auth?.permissions?.some((a) => a === 'STATISTIC_READ_ALL'),
      viewColumns: rootState.auth?.permissions?.filter((a) => STATISTIC_COLUMNS.some((b) => a === b)),
    }
  },
}

const mutations = {
  SET_ITEMS(state, items) {
    state.items = items.map((a) => {
      const currency = currencySymbols[a.currency] ?? a.currency
      return {
        ...a,
        uuid: uuidv4(),
        statSpend: `${a.statSpend} ${currency}`,
        cpc: `${a.cpc} ${currency}`,
        cpm: `${a.cpm} ${currency}`,
        cpv: `${a.cpv} ${currency}`,
        vtr: `${a.vtr} %`,
        completionRate: `${a.completionRate} %`,
        fillRate: `${a.fillRate} %`,
      }
    })
  },
  SET_TOTAL(state, total) {
    state.total = total ? formatMetrics(total) : undefined
  },
  SET_STATISTIC_GROUPS(state, groups) {
    state.groups = groups
  },
  SET_PAGINATION(state, data) {
    state.dataPagination = {
      pageNumber: data.number + 1,
      totalPages: data.totalPages,
    }
  },
  SET_PARAMS(state, params) {
    state.params = {
      ...state.params,
      ...params,
    }
  },
  DELETE_PARAM(state, param) {
    delete state.params[param.name]
  },
  SET_PAGE_NUMBER(state, {page}) {
    state.params = {
      ...state.params,
      page: page - 1,
    }
  },
  SET_ROW_SIZE(state, size = DEFAULT_SIZE) {
    state.params = {
      ...state.params,
      size,
    }
  },
  SET_SORT_PARAMS(state, {sortNameParam, sortSetParam}) {
    const sortStr = sortNameParam && sortSetParam ? `${sortNameParam},${sortSetParam}` : DEFAULT_PARAMS.sort
    state.params = {
      ...state.params,
      sort: sortStr,
    }
  },
  SET_DATES(state, dates) {
    state.params = {
      ...state.params,
      dateFrom: dates[0],
      dateTo: dates[1],
    }
  },
  SET_CLIENTS_STATISTICS(state, value = DEFAULT_PARAMS.isClientsStatistics) {
    state.params.isClientsStatistics = value
  },
  SET_TOTAL_STATISTICS(state, value = DEFAULT_PARAMS.isShowTotal) {
    state.params.isShowTotal = value
  },
  SET_USER_ID(state, value = DEFAULT_PARAMS.userId) {
    state.params.userId = value
  },
  SET_LOADING(state, {type, loading}) {
    switch (type) {
      case TYPE_LOADING:
        state.loading = loading
        break
      case TYPE_LOADING_ACTION:
        state.actionLoading = loading
        break
    }
  },
  RESET_PARAMS(state, isResetAll = true) {
    const dateFrom = isResetAll ? undefined : state.params.dateFrom
    const dateTo = isResetAll ? undefined : state.params.dateTo
    const groupParams = isResetAll ? undefined : state.params.groupParams
    const sort = isResetAll ? DEFAULT_PARAMS.sort : state.params.sort
    const size = state.params.size
    state.params = {...DEFAULT_PARAMS, dateFrom, dateTo, groupParams, sort, size}

    state.isShowFilterBar = false
    state.selectedAppFilter = {}
    state.selectedTableFilters = []
  },
  SET_APP_FILTER(state, {data = {}, isApi = false}) {
    state.isShowFilterBar = true

    if (isApi) {
      state.selectedAppFilter = data
    } else {
      const {value, placeholder, filterType, isObjectValue = false} = data
      state.selectedAppFilter = {
        name: value === 'page' ? 'pageF' : value,
        value: '',
        placeholder: placeholder ?? value,
        type: filterType,
        isObjectValue,
      }
    }
  },
  RESET_TABLE_FILTERS(state) {
    state.selectedTableFilters = []
  },
  SET_TABLE_FILTERS(state, data = undefined) {
    const {name} = state.selectedAppFilter
    const filtersToUpdate = data ?? state.selectedTableFilters

    if (!isDuplicateFilter(state.selectedTableFilters, name)) {
      addFilter(state, filtersToUpdate, data)
    } else {
      updateFilter(filtersToUpdate, state.selectedAppFilter)
    }

    const params = createParams(state.selectedTableFilters)

    // Сохраняем параметры и сбрасываем номер страницы
    this.commit('statistic/SET_PARAMS', params)
    this.commit('statistic/SET_PAGE_NUMBER', {page: 1})
  },
  DELETE_TABLE_FILTERS(state, data) {
    state.selectedTableFilters = state.selectedTableFilters.filter((a) => a.name !== data.name)
    this.commit('statistic/DELETE_PARAM', data)

    if (state.selectedTableFilters.length === 0) {
      state.isShowFilterBar = false
    } else {
      state.selectedAppFilter = state.selectedTableFilters[0]
    }
  },
}

const actions = {
  async GET_ITEMS({commit, dispatch}, params) {
    if (controller) {
      controller.abort()
    }
    controller = new AbortController()

    commit('SET_LOADING', {type: TYPE_LOADING, loading: true})
    commit('SET_ITEMS', [])
    try {
      const res = await axios.post('statistic/user/all', params, {signal: controller.signal})
      if (res.status === 200) {
        const {content, total} = res.data
        commit('SET_TOTAL', total)
        commit('SET_ITEMS', content)
        commit('SET_PAGINATION', res.data)
      }
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        dispatch('SHOW_TOAST_INFO', i18n.t('toast.cancelReportUpdate'), {root: true})
      } else {
        await customErrors(err, dispatch)
      }
    } finally {
      controller = null
      commit('SET_LOADING', {type: TYPE_LOADING, loading: false})
    }
  },
  CANCEL_GET_ITEMS() {
    if (controller) {
      controller.abort()
      controller = null
    }
  },
  async GET_STATISTIC_GROUPS({commit, dispatch}, params) {
    commit('SET_LOADING', {type: TYPE_LOADING_ACTION, loading: true})
    try {
      const body = {isClientsStatistics: params.isClientsStatistics}
      const res = await axios.get('dictionary/statistic/grouping-params/all', {params: body})
      if (res.status === 200) {
        commit('SET_STATISTIC_GROUPS', res.data)
      }
    } catch (err) {
      await customErrors(err, dispatch)
      commit('SET_STATISTIC_GROUPS', [])
    } finally {
      commit('SET_LOADING', {type: TYPE_LOADING_ACTION, loading: false})
    }
  },
  async EXPORT_FILE({commit, dispatch}, params) {
    try {
      const res = await axios.post('file/statistic/user-statistic-report/view', params, {
        responseType: 'blob',
      })
      const {data, status} = res
      if (data && status === 200) {
        await fileDownload(res)
        dispatch('SHOW_TOAST_SUCCESS', i18n.t('toast.fileDownload'), {root: true})
        return data
      }
    } catch (err) {
      await customErrors(err, dispatch)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
